/* global THREE */
import React, { useState, useEffect, useRef } from 'react';

const MP = 'https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1675469240';
const loadScript = src => new Promise((res, rej) => {
  if (document.querySelector(`script[src="${src}"]`)) return res();
  const s = document.createElement('script');
  s.src = src; s.async = true; s.onload = res; s.onerror = rej;
  document.head.appendChild(s);
});

/**
 * پرو واقعی: نیمه پشتی مدل پشت انگشت/مچ پنهان می‌شود (انسداد واقعی)،
 * نیمه جلویی روی آن می‌نشیند — انگار حلقه واقعاً رد شده است.
 * لایه‌ها: ویدیو < مدلِ پشت < پیکسل زنده انگشت < مدلِ جلو.
 */
export default function ARTryOn({ src, name, arType, arModel }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const backRef = useRef(null);
  const frontRef = useRef(null);
  const fingerRef = useRef(null);
  const [err, setErr] = useState('');
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 42 });
  const [scale, setScale] = useState(1);
  const [track, setTrack] = useState(null);
  const [tracking, setTracking] = useState(false);
  const drag = useRef(null);
  const touched = useRef(false);
  // مقادیر زنده برای حلقه رندر (جلوگیری از stale closure)
  const liveRef = useRef({ track: null, scale: 1, touched: false });
  liveRef.current.track = track;
  liveRef.current.scale = scale;
  liveRef.current.touched = touched.current;
  const isWrist = arType === 'wrist';
  const isFinger = arType === 'finger';
  const trackable = isWrist || isFinger;
  const use3D = trackable && !!arModel;

  useEffect(() => {
    let stream;
    let alive = true;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      } catch {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } catch (e) {
          if (alive) setErr('دسترسی به دوربین ممکن نشد؛ از تنظیمات مرورگر اجازه بدهید.');
          return;
        }
      }
      if (alive && videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch {}
        setReady(true);
      }
    })();
    return () => { alive = false; try { (stream || (videoRef.current && videoRef.current.srcObject))?.getTracks()?.forEach(t => t.stop()); } catch {} };
  }, []);

  const toPct = e => {
    const r = wrapRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return {
      x: Math.min(95, Math.max(5, ((p.clientX - r.left) / r.width) * 100)),
      y: Math.min(95, Math.max(5, ((p.clientY - r.top) / r.height) * 100)),
    };
  };
  const onDown = e => { touched.current = true; setTrack(null); drag.current = true; setPos(toPct(e)); };
  const onMove = e => { if (drag.current) setPos(toPct(e)); };
  const onUp = () => { drag.current = false; };

  // ردیابی: قاعده، نوک و پهنای عضو (مختصات نرمال فریم ویدیو) + هموارسازی
  const smoothRef = useRef(null);
  useEffect(() => {
    if (!trackable || !ready) return;
    let hands = null, raf = 0, alive = true, last = 0;
    (async () => {
      try {
        await loadScript(`${MP}/hands.js`);
        if (!alive || !window.Hands) return;
        hands = new window.Hands({ locateFile: f => `${MP}/${f}` });
        hands.setOptions({ maxNumHands: 1, modelComplexity: 1, minDetectionConfidence: 0.5, minTrackingConfidence: 0.4 });
        hands.onResults(res => {
          if (!alive) return;
          const lm = res.multiHandLandmarks && res.multiHandLandmarks[0];
          if (!lm) { setTracking(false); smoothRef.current = null; return; }
          let cur;
          if (isFinger) {
            const b = lm[13], tip = lm[16], n = lm[14];
            cur = {
              bx: b.x * 0.7 + n.x * 0.3, by: b.y * 0.7 + n.y * 0.3, bz: (b.z + n.z) / 2,
              tx: tip.x, ty: tip.y, tz: tip.z,
              wN: Math.hypot(n.x - b.x, n.y - b.y) * 0.45, kind: 'finger',
            };
          } else {
            const w = lm[0], m9 = lm[9];
            const palm = Math.hypot(lm[5].x - lm[17].x, lm[5].y - lm[17].y);
            const dx = m9.x - w.x, dy = m9.y - w.y, L = Math.hypot(dx, dy) || 1;
            cur = {
              bx: w.x, by: w.y, bz: w.z, tx: w.x + (dx / L) * palm * 1.1, ty: w.y + (dy / L) * palm * 1.1, tz: m9.z,
              wN: palm * 0.5, kind: 'wrist',
            };
          }
          // هموارسازی تطبیقی: حرکت آرام = ثبات بالا، حرکت تند = دنبال‌کردن سریع
          const p = smoothRef.current;
          const jump = p ? Math.hypot(cur.bx - p.bx, cur.by - p.by, (cur.tx - p.tx) * 0.7, (cur.ty - p.ty) * 0.7) : 1;
          const k = p ? Math.min(0.95, Math.max(0.3, jump * 12)) : 1;
          const s = p ? {
            bx: p.bx + (cur.bx - p.bx) * k, by: p.by + (cur.by - p.by) * k, bz: (p.bz || 0) + ((cur.bz || 0) - (p.bz || 0)) * k,
            tx: p.tx + (cur.tx - p.tx) * k, ty: p.ty + (cur.ty - p.ty) * k, tz: (p.tz || 0) + ((cur.tz || 0) - (p.tz || 0)) * k,
            wN: p.wN + (cur.wN - p.wN) * k, kind: cur.kind,
          } : cur;
          smoothRef.current = s;
          setTrack(s);
          setTracking(true);
        });
        const loop = async t => {
          if (!alive) return;
          if (t - last > 80 && videoRef.current && videoRef.current.readyState >= 2 && !touched.current) {
            last = t;
            try {
              // تطبیقی: موقع جست‌وجو فریم کوچک (سریع)، موقع قفل فریم بزرگ‌تر (دقیق)
              const locked = !!smoothRef.current;
              const W = locked ? 384 : 256;
              const vw = videoRef.current.videoWidth, vh = videoRef.current.videoHeight;
              if (!loop.small || loop.smallW !== W) { loop.small = document.createElement('canvas'); loop.small.width = W; loop.small.height = Math.max(2, Math.round(W * vh / Math.max(1, vw))); loop.smallW = W; }
              const sx = loop.small.getContext('2d');
              sx.drawImage(videoRef.current, 0, 0, loop.small.width, loop.small.height);
              await hands.send({ image: loop.small });
            } catch {}
          }
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      } catch { /* بدون ردیابی ادامه بده — حالت دستی */ }
    })();
    return () => { alive = false; cancelAnimationFrame(raf); try { hands && hands.close(); } catch {} };
  }, [trackable, ready, isFinger]); // eslint-disable-line react-hooks/exhaustive-deps

  // صحنه سه‌بعدی دوپاسه: پشت (پشت انگشت) و جلو (روی انگشت)
  const gl = useRef(null);
  useEffect(() => {
    if (!use3D || !ready) return;
    let alive = true, raf = 0;
    (async () => {
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/three@0.147.0/build/three.min.js');
        await loadScript('https://cdn.jsdelivr.net/npm/three@0.147.0/examples/js/loaders/GLTFLoader.js');
        await loadScript('https://cdn.jsdelivr.net/npm/three@0.147.0/examples/js/loaders/RGBELoader.js').catch(() => {});
        await loadScript('https://cdn.jsdelivr.net/npm/three@0.147.0/examples/js/environments/RoomEnvironment.js').catch(() => {});
        if (!alive || !window.THREE || !backRef.current) return;
        const T = window.THREE;
        const cw0 = wrapRef.current.clientWidth || 2, ch0 = wrapRef.current.clientHeight || 2;
        // HDRI استودیو یک‌بار برای هر دو صحنه (با همان رندرر اول — بدون کانتکست اضافه)
        const mk = canvas => {
          const r = new T.WebGLRenderer({ canvas, alpha: true, antialias: true });
          r.setSize(cw0, ch0, false);
          r.outputEncoding = T.sRGBEncoding;
          r.toneMapping = T.ACESFilmicToneMapping;
          r.toneMappingExposure = 1.35;
          r.localClippingEnabled = true;
          const sc = new T.Scene();
          sc.userData.pendingEnv = true;
          const cam = new T.PerspectiveCamera(50, cw0 / ch0, 0.01, 100);
          cam.position.set(0, 0, 3);
          sc.add(new T.AmbientLight(0xffffff, 1.2));
          const dir = new T.DirectionalLight(0xffffff, 1.6);
          dir.position.set(2, 3, 4);
          sc.add(dir);
          return { r, sc, cam };
        };
        const B = mk(backRef.current), F = mk(frontRef.current);
        // محیط استودیویی واقعی (HDRI) — جدا برای هر رندرر (تکسچر یک کانتکست در دیگری سیاه می‌شود)
        for (const { r, sc } of [B, F]) {
          try {
            if (T.RGBELoader) {
              const hdr = await new T.RGBELoader().loadAsync('https://demo-jewelry.sitebuilder.jmsoft.ir/storage/ar/studio.hdr');
              const pm = new T.PMREMGenerator(r);
              sc.environment = pm.fromEquirectangular(hdr).texture;
              hdr.dispose(); pm.dispose();
            } else throw new Error('no-rgbe');
          } catch {
            try {
              const pm = new T.PMREMGenerator(r);
              sc.environment = pm.fromScene(new T.RoomEnvironment(), 0.06).texture;
            } catch {}
          }
        }
        const gltf = await new T.GLTFLoader().loadAsync(arModel);
        if (!alive) return;
        // مدل در z=0 می‌نشیند؛ صفحه برش جهانی: پشت z≤۰، جلو z≥۰
        const prep = (srcScene, flip) => {
          const m = srcScene.clone(true);
          const plane = new T.Plane(new T.Vector3(0, 0, flip ? 1 : -1), 0);
          m.traverse(o => {
            if (o.isMesh) {
              o.material = Array.isArray(o.material) ? o.material.map(x => x.clone()) : o.material.clone();
              (Array.isArray(o.material) ? o.material : [o.material]).forEach(mt => {
                mt.clippingPlanes = [plane];
                const nm = (mt.name || '').toLowerCase();
                if (nm.includes('diamond') && T.MeshPhysicalMaterial) {
                  // نگین روشنِ همیشه (emissive کم تا هیچ‌وقت سیاه نماند) + دوطرفه برای برش
                  const phys = new T.MeshPhysicalMaterial({
                    color: 0xffffff, metalness: 0.1, roughness: 0.03,
                    envMapIntensity: 2.5, clearcoat: 1, clearcoatRoughness: 0.03,
                    emissive: 0xbbbbcc, emissiveIntensity: 0.55, side: T.DoubleSide,
                  });
                  phys.clippingPlanes = [plane];
                  o.material = phys;
                } else {
                  mt.envMapIntensity = 1.6;
                  if ('side' in mt) mt.side = T.DoubleSide;
                }
              });
            }
          });
          return m;
        };
        const modelB = prep(gltf.scene, false);
        const modelF = prep(gltf.scene, true);
        B.sc.add(modelB);
        F.sc.add(modelF);
        const V3 = (x, y, z) => new T.Vector3(x, y, z);
        const loop = () => {
          if (!alive) return;
          const v = videoRef.current;
          const cw = wrapRef.current.clientWidth, ch = wrapRef.current.clientHeight;
          const L = liveRef.current;
          if (v && v.videoWidth && L.track && !L.touched) {
            const vw = v.videoWidth, vh = v.videoHeight;
            const s = Math.max(cw / vw, ch / vh);
            const ox = (cw - vw * s) / 2, oy = (ch - vh * s) / 2;
            const P = (nx, ny) => ({ x: ox + nx * vw * s, y: oy + ny * vh * s });
            const b = P(L.track.bx, L.track.by), tp = P(L.track.tx, L.track.ty);
            const wPx = Math.max(8, L.track.wN * vw * s);
            const halfH = 3 * Math.tan(25 * Math.PI / 180), halfW = halfH * (cw / ch);
            const W = (px, py) => ({ x: ((px - cw / 2) / (cw / 2)) * halfW, y: -(((py - ch / 2) / (ch / 2)) * halfH) });
            const wb = W(b.x, b.y), wt = W(tp.x, tp.y);
            const cx = wb.x * 0.75 + wt.x * 0.25, cy = wb.y * 0.75 + wt.y * 0.25;
            // راستای سه‌بعدی عضو با عمق مدیاپایپ — دست را بچرخانی حلقه هم می‌چرخد و پشتش دیده می‌شود
            const ZZ = 1.2;
            const A = V3(wt.x - wb.x, wt.y - wb.y, ((L.track.tz || 0) - (L.track.bz || 0)) * ZZ);
            if (A.length() < 1e-4) A.set(0, 1, 0);
            A.normalize();
            const upC = V3(0, 0, 1).addScaledVector(A, -A.z).normalize();
            const fwW = (wPx / (cw / 2)) * halfW;
            const inner = L.track.kind === 'finger' ? 1.74 : 1.82;
            const sc = Math.max(0.05, Math.min(1.3, (fwW * 1.05) / inner * L.scale));
            [modelB, modelF].forEach(mm => {
              mm.position.set(cx, cy, Math.max(-0.6, Math.min(0.6, (L.track.bz || 0) * 1.5)));
              // سوراخ حلقه هم‌محور با انگشت/مچ + چرخش دور محور تا نگین رو به بیننده باشد
              mm.up.copy(upC);
              mm.lookAt(cx + A.x, cy + A.y, 0 + A.z);
              const yW = V3(0, 1, 0).applyQuaternion(mm.quaternion);
              yW.addScaledVector(A, -yW.dot(A));
              if (yW.length() > 1e-4) {
                yW.normalize();
                const camD = V3(0, 0, 1).addScaledVector(A, -A.z).normalize();
                mm.rotateZ(Math.atan2(yW.clone().cross(camD).dot(A), yW.dot(camD)));
              }
              mm.scale.set(sc, sc, sc);
            });
            B.r.render(B.sc, B.cam);
            // کپسول انگشت = ناحیه برش (بدون هیچ رنگ/خط — فقط clip، وگرنه سیاه می‌ماند)
            const fx = fingerRef.current.getContext('2d');
            if (fingerRef.current.width !== cw) { fingerRef.current.width = cw; fingerRef.current.height = ch; }
            fx.clearRect(0, 0, cw, ch);
            fx.save();
            fx.beginPath();
            const angP = Math.atan2(tp.y - b.y, tp.x - b.x);
            const ex = wPx * 0.15;
            const x1 = b.x - Math.cos(angP) * ex, y1 = b.y - Math.sin(angP) * ex;
            const x2 = tp.x + Math.cos(angP) * ex, y2 = tp.y + Math.sin(angP) * ex;
            const nx = -Math.sin(angP), ny = Math.cos(angP);
            const aN = Math.atan2(ny, nx);
            const h = wPx / 2;
            fx.arc(x2, y2, h, aN - Math.PI / 2, aN + Math.PI / 2, false);
            fx.arc(x1, y1, h, aN + Math.PI / 2, aN + Math.PI * 1.5, false);
            fx.closePath();
            fx.clip();
            fx.drawImage(v, ox, oy, vw * s, vh * s);
            fx.restore();
            F.r.render(F.sc, F.cam);
          } else {
            const sc0 = 0.85 * liveRef.current.scale;
            [modelB, modelF].forEach(mm => {
              mm.position.set(0, 0, 0);
              mm.rotation.set(0.25, mm.rotation.y + 0.01, 0);
              mm.scale.set(sc0, sc0, sc0);
            });
            B.r.render(B.sc, B.cam);
            const fx = fingerRef.current.getContext('2d');
            fx.clearRect(0, 0, cw, ch);
            F.r.render(F.sc, F.cam);
          }
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        gl.current = { B, F };
      } catch { gl.current = null; }
    })();
    return () => { alive = false; cancelAnimationFrame(raf); try { gl.current?.B.r.dispose(); gl.current?.F.r.dispose(); } catch {} gl.current = null; };
  }, [use3D, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const shot = () => {
    try {
      const v = videoRef.current;
      const c = document.createElement('canvas');
      c.width = v.videoWidth || 720; c.height = v.videoHeight || 1280;
      const ctx = c.getContext('2d');
      const vr = v.videoWidth / v.videoHeight, cr = c.width / c.height;
      let sw, sh, sx, sy;
      if (vr > cr) { sh = v.videoHeight; sw = sh * cr; sx = (v.videoWidth - sw) / 2; sy = 0; }
      else { sw = v.videoWidth; sh = sw / cr; sx = 0; sy = (v.videoHeight - sh) / 2; }
      ctx.drawImage(v, sx, sy, sw, sh, 0, 0, c.width, c.height);
      const done = () => {
        const a = document.createElement('a');
        a.href = c.toDataURL('image/png');
        a.download = 'ar-tryon.png';
        a.click();
      };
      if (use3D && frontRef.current) {
        const img = new Image();
        img.onload = () => { ctx.drawImage(img, 0, 0, c.width, c.height); done(); };
        img.src = frontRef.current.toDataURL('image/png');
      } else if (!use3D && src) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const w = c.width * 0.45 * scale;
          const h = w * (img.height / img.width);
          ctx.drawImage(img, (c.width * pos.x) / 100 - w / 2, (c.height * pos.y) / 100 - h / 2, w, h);
          done();
        };
        img.src = src;
      } else done();
    } catch {}
  };

  if (err) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{err}</div>
        {src && <img src={src} alt={name} style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }} />}
      </div>
    );
  }

  return (
    <div>
      <div ref={wrapRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={e => { touched.current = true; setTrack(null); drag.current = true; setPos(toPct(e)); }} onTouchMove={e => { if (drag.current) { e.preventDefault(); setPos(toPct(e)); } }} onTouchEnd={onUp}
        style={{ position: 'relative', width: '100%', height: 'min(62vh, 480px)', background: '#000', overflow: 'hidden', touchAction: 'none', cursor: 'move' }}>
        <video ref={videoRef} playsInline muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        {!ready && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem' }}>در حال روشن شدن دوربین…</div>}
        {use3D ? (
          <>
            <canvas ref={backRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
            <canvas ref={fingerRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
            <canvas ref={frontRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
          </>
        ) : (
        src && <img src={src} alt={name} draggable={false}
          style={(() => {
            const auto = trackable && track && !touched.current;
            const wPct = 34 * scale;
            return {
              position: 'absolute', left: `${auto ? 50 : pos.x}%`, top: `${auto ? 50 : pos.y}%`, transform: 'translate(-50%,-50%)',
              width: `${wPct}%`, maxWidth: 340, pointerEvents: 'none',
              filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.5))',
            };
          })()}
        />
        )}
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: '0.72rem', opacity: 0.85, pointerEvents: 'none' }}>
          {trackable ? (tracking ? `${isFinger ? 'حلقه دور انگشت نشست ✓' : 'دستبند دور مچ نشست ✓'} — برای تنظیم دستی بکشید` : `${isFinger ? 'انگشتتان' : 'دستتان'} را جلوی دوربین بگیرید…`) : 'محصول را با انگشت روی تصویر بکشید و با اسلایدر اندازه کنید'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--muted-fg)' }}>اندازه</span>
        <input type="range" min="0.4" max="2.2" step="0.05" value={scale} onChange={e => setScale(Number(e.target.value))} style={{ flex: 1 }} aria-label="اندازه محصول" />
        <button onClick={shot} style={{ border: '1px solid var(--border)', background: 'var(--primary)', color: 'var(--on-primary)', borderRadius: 10, padding: '9px 18px', fontWeight: 800, fontFamily: 'inherit', fontSize: '0.82rem', cursor: 'pointer' }}>
          عکس گرفتن
        </button>
      </div>
    </div>
  );
}
